// The home hub's own copy — welcome header, Zone 1 prompt ladder, the
// next-session hero, and the two archive drill cards below it. #1220
// established the copy-module convention; the sibling `homeUpcomingCopy.ts`
// already owns the "Upcoming" section's own strings and is extended here
// with the section eyebrow.
//
// Deliberately NOT here: the reschedule-offer CTA copy (`rescheduleCopy.ts`
// via `RescheduleOfferCtas`), the per-session change actions (`SESSION_CHANGE_COPY`
// via `SessionChangeActions`), the join-state captions (`SESSION_JOIN_CAPTIONS`
// in `sessionJoinState.ts`), and session date/time formatting — all owned
// elsewhere or FORMATTING, not this page's copy.
//
// Flat rather than nested by design: every string here is reached in one hop
// from both this page and its suite.

/** Matches the flow-wide fallback: lowercase, so it can never open a sentence. */
export const FALLBACK_THERAPIST_NAME = "your therapist";

export const HOME_HUB_COPY = {
  eyebrow: "Home",
  welcomeBack: (firstName: string | undefined) => `Welcome back${firstName ? `, ${firstName}` : ""}`,
  loadingLabel: "Loading home…",
  inviteLinkCopiedLabel: "Invite link copied",

  nextSessionEyebrow: "Next session",
  sessionMeta: (sessionNumber: number, therapistName: string) =>
    `Session ${sessionNumber} with ${therapistName}`,
  /** The session location, shown after the meta with its icon. */
  sessionLocationVideo: "Video Call",
  sessionLocationInPerson: "In Person",
  /** The drill cards' trailing label (Past sessions, Weekly reports). */
  viewAllLabel: "View all",
  /** The disabled Join button's label before the window: the actual time joining opens, in the
   *  session's tz. Authored in Title Case (the button's own `capitalize` would also raise "from"). */
  joinOpensAt: (time: string) => `Join from ${time}`,
  joinSessionLabel: "Join session",
  nothingBookedTitle: "Nothing booked yet",
  noSessionBodyWithHistory: (lastSessionDate: string, therapistName: string) =>
    `Your last session was ${lastSessionDate} with ${therapistName}.`,
  noSessionBodyNoHistory: "Your next session will appear here once it’s booked.",
  findTimeBody: "Find a time that works for you both.",

  reportCountLabel: (total: number) => (total === 1 ? "1 report" : `${total} reports`),
  pastCountLabel: (total: number) => (total === 1 ? "1 session" : `${total} sessions`),

  reportsLabel: "Weekly reports",
  reportsTitle: "Your week, together",
  reportsMeta: (reportCountLabel: string) => `${reportCountLabel} · shared with you both`,

  pastSessionsLabel: "Past sessions",
  pastSessionsTitle: (total: number) => `Sessions 1-${total}`,
  pastSessionsMetaWithRecent: (lastSessionDate: string, pastCountLabel: string) =>
    `Most recent ${lastSessionDate} · ${pastCountLabel}`,

  chipStartsIn: (minutes: number) => `Starts in ${minutes} min`,
  chipReadyNow: "Ready now",
  chipInDays: (days: number) => `in ${days} days`,
  chipInHours: (hours: number) => `in ${hours} hr`,
  chipInMinutes: (minutes: number) => `in ${minutes} min`,

  inviteEyebrow: "Get set up",
  inviteTitle: "Dova works better together",
  inviteMeta: "Invite your partner so Dova can support you both between sessions.",
  inviteActionLabel: "Invite partner",

  feedbackEyebrow: "Last Session",
  feedbackTitle: "How was your last session?",
  feedbackMeta: (dateLabel: string | null, sessionNumber: number) =>
    dateLabel ? `${dateLabel} • Session ${sessionNumber}` : `Session ${sessionNumber}`,
  feedbackActionLabel: "Share Feedback",

  checkinEyebrow: "Before your session",
  checkinPendingTitle: "Check in before your session?",
  // No therapist named: no therapist surface reads check-ins yet.
  checkinPendingMeta: "A few quick questions before your session, about 3 minutes.",
  checkinPendingActionLabel: "Start check-in",

  reportPromptEyebrow: "Weekly report",
  reportPromptMeta: "A calm read on your week · shared with you both",
  reportPromptActionLabel: "Read report",
} as const;
